"use client"

import { useState } from "react"
import { GlowCard } from "@/components/crystality/glow-card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Mail, Loader2, CheckCircle, AlertTriangle } from "lucide-react"
import { toast } from "sonner"

export function NewsletterSignup() {
  const [email, setEmail] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address")
      return
    }

    setIsSubmitting(true)
    try {
      const response = await fetch("/api/newsletter/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || "Failed to sign up")
      }

      setIsSubmitted(true)
      toast.success(data.message || "You've been added to the waitlist!")
      setEmail("")
    } catch (error: any) {
      toast.error(error.message || "Failed to sign up for notifications")
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isSubmitted) {
    return (
      <GlowCard className="p-6 text-center">
        <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold mb-2">You're on the list! 🎉</h3>
        <p className="text-muted-foreground">
          We'll notify you as soon as registration opens again.
        </p>
      </GlowCard>
    )
  }

  return (
    <GlowCard className="p-6">
      <div className="flex items-start gap-4 mb-6">
        <div className="p-3 rounded-lg bg-yellow-500/10">
          <AlertTriangle className="h-6 w-6 text-yellow-500" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold mb-2">Registration Currently Closed</h3>
          <p className="text-sm text-muted-foreground">
            To maintain the quality and exclusivity of Crystality, we're currently limiting new signups. 
            Join our waitlist to be notified when registration opens again!
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="newsletter-email" className="text-foreground">
            Email Address
          </Label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="newsletter-email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 bg-secondary border-border"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="gradient-purple text-white"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Joining...
                </>
              ) : (
                "Notify Me"
              )}
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          We'll send you an email as soon as registration opens. No spam, unsubscribe anytime.
        </p>
      </form>
    </GlowCard>
  )
}
